/**
 * Tawk.to Live Chat Integration Service
 * Provides methods to load, show, hide, and interact with Tawk.to chat widget
 */

// Tawk.to configuration
// Replace these with your actual Tawk.to Property ID and Widget ID
const TAWK_PROPERTY_ID = import.meta.env.VITE_TAWK_PROPERTY_ID || '6931f4cafc6441197ee01662'
const TAWK_WIDGET_ID = import.meta.env.VITE_TAWK_WIDGET_ID || '1jbli8eb1'

// Global Tawk API interface
declare global {
  interface Window {
    Tawk_API?: {
      onLoad?: () => void
      onStatusChange?: (status: string) => void
      customStyle?: {
        visibility?: {
          desktop?: { position?: string; xOffset?: number; yOffset?: number }
          mobile?: { position?: string; xOffset?: number; yOffset?: number }
          bubble?: { rotate?: string; xOffset?: number; yOffset?: number }
        }
      }
      showWidget?: () => void
      hideWidget?: () => void
      toggle?: () => void
      maximize?: () => void
      minimize?: () => void
      popup?: () => void
      setAttributes?: (attributes: Record<string, string>, callback?: (error: any) => void) => void
      addEvent?: (event: string, metadata?: Record<string, any>, callback?: (error: any) => void) => void
      addTags?: (tags: string[], callback?: (error: any) => void) => void
      removeTags?: (tags: string[], callback?: (error: any) => void) => void
      visitor?: Record<string, any>
      isChatMaximized?: () => boolean
      isChatMinimized?: () => boolean
      isChatHidden?: () => boolean
      isChatOngoing?: () => boolean
      endChat?: () => void
    }
    Tawk_LoadStart?: Date
  }
}

let isLoaded = false
let isLoading = false

/**
 * Load Tawk.to chat widget script
 */
export const loadTawkTo = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (isLoaded && window.Tawk_API) {
      resolve()
      return
    }

    // Currently loading
    if (isLoading) {
      const checkInterval = setInterval(() => {
        if (isLoaded && window.Tawk_API) {
          clearInterval(checkInterval)
          resolve()
        }
      }, 100)
      return
    }

    isLoading = true

    // Check if script already exists
    const existingScript = document.getElementById('tawk-script')
    if (existingScript) {
      existingScript.remove()
    }

    // Validate configuration
    if (!TAWK_PROPERTY_ID || TAWK_PROPERTY_ID === 'your_property_id_here') {
      console.warn('Tawk.to Property ID not configured')
      isLoading = false
      reject(new Error('Tawk.to Property ID not configured'))
      return
    }

    if (!TAWK_WIDGET_ID || TAWK_WIDGET_ID === 'default') {
      console.warn('Tawk.to Widget ID not configured properly')
      isLoading = false
      reject(new Error('Tawk.to Widget ID not configured'))
      return
    }

    // Nudge the widget bubble inward from the screen edge so it doesn't get
    // clipped on tablet/iPad viewports. customStyle must be set BEFORE the
    // embed script loads, so seed Tawk_API here.
    if (!window.Tawk_API) {
      window.Tawk_API = {}
    }
    window.Tawk_API.customStyle = {
      visibility: {
        desktop: { position: 'br', xOffset: 24, yOffset: 24 },
        mobile: { position: 'br', xOffset: 12, yOffset: 16 },
      },
    }

    // Create Tawk.to script
    const script = document.createElement('script')
    script.id = 'tawk-script'
    script.async = true
    script.src = `https://embed.tawk.to/${TAWK_PROPERTY_ID}/${TAWK_WIDGET_ID}`
    script.charset = 'UTF-8'
    script.setAttribute('crossorigin', '*')

    script.onload = () => {
      isLoaded = true
      isLoading = false
      
      // Initialize Tawk API
      if (!window.Tawk_API) {
        window.Tawk_API = {}
      }
      
      window.Tawk_LoadStart = new Date()
      
      // Wait for Tawk to be fully loaded
      const checkTawk = setInterval(() => {
        if (window.Tawk_API && window.Tawk_API.onLoad) {
          clearInterval(checkTawk)
          console.log('Tawk.to chat loaded successfully')
          resolve()
        }
      }, 100)
      
      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkTawk)
        if (!window.Tawk_API?.onLoad) {
          console.warn('Tawk.to load timeout')
        }
        resolve()
      }, 10000)
    }

    script.onerror = () => {
      isLoading = false
      console.error('Failed to load Tawk.to script')
      reject(new Error('Failed to load Tawk.to'))
    }

    document.head.appendChild(script)
  })
}

/**
 * Show Tawk.to chat widget
 */
export const showTawkWidget = () => {
  if (window.Tawk_API?.showWidget) {
    window.Tawk_API.showWidget()
  }
}

/**
 * Hide Tawk.to chat widget
 */
export const hideTawkWidget = () => {
  if (window.Tawk_API?.hideWidget) {
    window.Tawk_API.hideWidget()
  }
}

/**
 * Toggle Tawk.to chat widget visibility
 */
export const toggleTawkWidget = () => {
  if (window.Tawk_API?.toggle) {
    window.Tawk_API.toggle()
  }
}

/**
 * Maximize (open) Tawk.to chat window
 */
export const maximizeTawkChat = () => {
  if (window.Tawk_API?.maximize) {
    window.Tawk_API.maximize()
  }
}

/**
 * Minimize (close) Tawk.to chat window
 */
export const minimizeTawkChat = () => {
  if (window.Tawk_API?.minimize) {
    window.Tawk_API.minimize()
  }
}

/**
 * Open Tawk.to chat as popup
 */
export const openTawkPopup = () => {
  if (window.Tawk_API?.popup) {
    window.Tawk_API.popup()
  }
}

/**
 * Set user attributes in Tawk.to
 */
export const setTawkAttributes = (attributes: {
  name?: string
  email?: string
  hash?: string
  [key: string]: string | undefined
}) => {
  if (window.Tawk_API?.setAttributes) {
    const validAttributes: Record<string, string> = {}
    Object.entries(attributes).forEach(([key, value]) => {
      if (value) validAttributes[key] = value
    })
    
    window.Tawk_API.setAttributes(validAttributes, (error) => {
      if (error) {
        console.error('Failed to set Tawk attributes:', error)
      }
    })
  }
}

/**
 * Add event to Tawk.to
 */
export const addTawkEvent = (event: string, metadata?: Record<string, any>) => {
  if (window.Tawk_API?.addEvent) {
    window.Tawk_API.addEvent(event, metadata, (error) => {
      if (error) {
        console.error('Failed to add Tawk event:', error)
      }
    })
  }
}

/**
 * Add tags to visitor in Tawk.to
 */
export const addTawkTags = (tags: string[]) => {
  if (window.Tawk_API?.addTags) {
    window.Tawk_API.addTags(tags, (error) => {
      if (error) {
        console.error('Failed to add Tawk tags:', error)
      }
    })
  }
}

/**
 * Check if chat is maximized
 */
export const isTawkChatMaximized = (): boolean => {
  return window.Tawk_API?.isChatMaximized?.() ?? false
}

/**
 * Check if chat is hidden
 */
export const isTawkChatHidden = (): boolean => {
  return window.Tawk_API?.isChatHidden?.() ?? true
}

/**
 * End current chat session
 */
export const endTawkChat = () => {
  if (window.Tawk_API?.endChat) {
    window.Tawk_API.endChat()
  }
}

/**
 * Initialize Tawk.to with user information
 */
export const initializeTawkWithUser = async (user: {
  name?: string
  email?: string
  userId?: string
}) => {
  try {
    await loadTawkTo()
    
    const attributes: Record<string, string> = {}
    if (user.name) attributes.name = user.name
    if (user.email) attributes.email = user.email
    
    if (Object.keys(attributes).length > 0) {
      setTawkAttributes(attributes)
    }
    
    if (user.userId) {
      addTawkTags([`user-${user.userId}`])
    }
  } catch (error) {
    console.error('Failed to initialize Tawk with user:', error)
  }
}

/**
 * Open support chat
 */
export const openSupportChat = async () => {
  try {
    await loadTawkTo()
    maximizeTawkChat()
  } catch (error) {
    console.error('Failed to open support chat:', error)
  }
}

export default {
  loadTawkTo,
  showTawkWidget,
  hideTawkWidget,
  toggleTawkWidget,
  maximizeTawkChat,
  minimizeTawkChat,
  openTawkPopup,
  setTawkAttributes,
  addTawkEvent,
  addTawkTags,
  isTawkChatMaximized,
  isTawkChatHidden,
  endTawkChat,
  initializeTawkWithUser,
  openSupportChat,
}
